/**
 * ImageStager — pick an image client-side, validate it, preview it.
 *
 * Differs from ImageUpload in that it does NOT upload the file. Used by
 * the "create entity" forms that need to attach an image to a record
 * which doesn't exist yet: the parent stages the File here, creates the
 * entity, and then calls uploadAttachment(stagedFile, { linked_entity_*
 * = newId }) to link the upload after the row has an id.
 *
 * Validation matches the server: 20 MB cap, JPEG / PNG / WebP / HEIC.
 * Failures surface as toasts and the picker stays empty rather than
 * accepting an invalid file (so the form can submit safely either way).
 */
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ImagePlus, X } from 'lucide-react';
import { toast } from 'sonner';

const ACCEPT = 'image/jpeg,image/png,image/webp,image/heic';
const MAX_BYTES = 20 * 1024 * 1024;

interface ImageStagerProps {
  /** Currently staged file, or null. */
  file: File | null;
  /** Called whenever the staged file changes. Receives null on clear. */
  onChange: (file: File | null) => void;
  /** Visible label for the picker button. Defaults to "Add image". */
  label?: string;
  /** Disable the picker (e.g. during submit). */
  disabled?: boolean;
  className?: string;
}

export function ImageStager({
  file,
  onChange,
  label,
  disabled,
  className,
}: ImageStagerProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);

  // Build/tear down an object URL for the preview. URL.revokeObjectURL
  // on cleanup keeps us from leaking blob URLs as the user re-picks.
  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handlePick = (): void => {
    inputRef.current?.click();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const picked = e.target.files?.[0];
    // Reset the input so the same file can be re-selected after a clear.
    e.target.value = '';
    if (!picked) return;

    if (picked.size > MAX_BYTES) {
      toast.error(
        `Image too large (max 20 MB; this one is ${(picked.size / 1024 / 1024).toFixed(1)} MB)`,
      );
      return;
    }
    if (!ACCEPT.split(',').includes(picked.type)) {
      toast.error('Unsupported image format. Use JPEG, PNG, WebP, or HEIC.');
      return;
    }
    onChange(picked);
  };

  const handleClear = (): void => {
    onChange(null);
  };

  return (
    <div className={className}>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        onChange={handleChange}
        className="hidden"
        aria-hidden="true"
      />
      {preview ? (
        <div className="flex items-center gap-3 rounded-lg border border-border bg-surface-raised/50 p-2 dark:bg-surface-raised-dark/50">
          <div className="h-16 w-16 shrink-0 overflow-hidden rounded-md border border-border bg-surface dark:bg-surface-dark">
            <img src={preview} alt="" className="h-full w-full object-cover" />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <p className="truncate text-sm" title={file?.name ?? ''}>
              {file?.name}
            </p>
            <p className="font-mono text-xs text-muted-foreground">
              {file ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : ''}
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleClear}
            disabled={disabled}
            aria-label="Remove image"
            className="h-8 w-8 shrink-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handlePick}
          disabled={disabled}
          className="w-full justify-start"
        >
          <ImagePlus className="h-4 w-4" />
          <span className="ml-2">{label ?? 'Add image'}</span>
        </Button>
      )}
    </div>
  );
}
